// Org chart — bulk manager editor + minimal chart view.
// tickets/sonnet.md "org chart: UI (admin editor screen + chart view)".
// Server half already shipped (server 424165e8, sdk 5f1396a):
//   sdk.getService('parties').list({kind:'person'})            — roster
//   sdk.getService('parties').listAllRelationships({kind:'reports_to'}) — edges
//   sdk.getService('parties').setManagers([{partyId,managerId}])       — bulk write (admin)
// This file is the data/state layer; components/OrgChartModal.js is the view.
//
// Party is a separate workspace-scoped identity from root.people (a Party
// row is auto-created 1:1 per (workspace, User) on every member-add —
// server ensurePartyForMember.js). The relationship edge direction is
// `a reports_to b` — a's manager is b.
//
// Joined to root.people on EMAIL, not on party.user === person.userId —
// live-verified 2026-08-16: root.people[].userId is the SUPABASE auth
// uuid (e.g. "43661a1c-b321-..."), while Party.user is the Mongo
// User._id (e.g. "6868484c0cf470c5890933cc") for the SAME person — the
// exact id-space gap ensurePartyForMember.js's own header comment
// documents ("userId MUST be the Mongo User._id, never the Supabase auth
// uuid"). The two ids can never string-match; email is the one identity
// signal both sides actually carry the same value for (confirmed against
// three real linked Party docs in dev).
//
// workspaceId resolution goes through this.call('activeWorkspaceId', root)
// rather than a sibling import of scopedClient.js — FA001, no imports
// between project files even within functions/.

// root.people (name/avatar/email) joined to the loaded Party roster
// (root._orgChartParties) on email, case-insensitive (see the id-space
// note above). A roster entry with no matching Party means the load
// hasn't settled yet, or this person has no Party row at all (a real gap
// this pass found: ensurePartyForMember isn't backfilled for most
// existing workspaces) — skipped rather than rendered broken.
// el.call('orgChartDirectory')
export function orgChartDirectory() {
  const root = this && this.state && this.state.root
  const parties = root?._orgChartParties || []
  const people = root?.people || []
  const byEmail = new Map(
    parties.filter((p) => p.primaryEmail).map((p) => [String(p.primaryEmail).toLowerCase(), p])
  )
  const rows = []
  for (const person of people) {
    const email = String(person.email || '').toLowerCase()
    const party = email ? byEmail.get(email) : null
    if (!party) continue
    rows.push({
      partyId: party.id,
      // No third fallback: a person with no email never reaches this line (the
      // join above skips them), so a nameless member reads as their email.
      name: person.name || person.email,
      avatar: person.avatar || null,
      email: person.email || null
    })
  }
  return rows.sort((a, b) => a.name.localeCompare(b.name))
}

// Map<partyId, managerPartyId> from the last-loaded reports_to edges.
// el.call('orgChartManagerOf')
export function orgChartManagerOf() {
  const root = this && this.state && this.state.root
  const edges = root?._orgChartEdges || []
  const map = new Map()
  for (const e of edges) {
    if (e.kind === 'reports_to' && e.a) map.set(String(e.a), e.b != null ? String(e.b) : null)
  }
  return map
}

// Map<managerPartyId|'root', partyId[]> — the inverse, for the tree view.
// An edge whose manager id isn't in the loaded roster (a stale edge racing
// a roster refresh — the server itself guarantees both ends exist at write
// time) falls back to 'root' rather than dropping the person or throwing.
// el.call('orgChartChildrenOf')
export function orgChartChildrenOf() {
  const directory = orgChartDirectory.call(this)
  const knownIds = new Set(directory.map((p) => p.partyId))
  const managerOf = orgChartManagerOf.call(this)
  const map = new Map()
  for (const p of directory) {
    const mgr = managerOf.get(p.partyId)
    const key = mgr && knownIds.has(mgr) ? mgr : 'root'
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(p.partyId)
  }
  return map
}

// Fetch the roster + edges, seed the editable working copy, open the modal.
// el.call('orgChartOpen', s)
export async function orgChartOpen(s) {
  const el = this
  const root = s.root
  root.update({ _orgChartOpen: true, _orgChartLoading: true, _orgChartLoadError: null })
  const workspaceId = this.call('activeWorkspaceId', root)
  try {
    // getService() is itself async (SDK init-gated — resolves to the real
    // service only once boot settles, a bare Promise facade otherwise), so
    // it must be awaited BEFORE calling a method on the result — chaining
    // .list(...) straight off the unresolved call throws "not a function".
    const parties = await el.sdk.getService('parties')
    const [rows, edges] = await Promise.all([
      parties.list({ kind: 'person' }, { workspaceId }),
      parties.listAllRelationships({ kind: 'reports_to' }, { workspaceId })
    ])
    const working = {}
    for (const e of edges || []) {
      if (e.kind === 'reports_to' && e.a) working[String(e.a)] = e.b != null ? String(e.b) : null
    }
    root.update({
      _orgChartParties: rows || [],
      _orgChartEdges: edges || [],
      _orgChartWorking: working,
      _orgChartLoading: false,
      _orgChartSaveError: null
    })
  } catch (err) {
    root.update({
      _orgChartLoading: false,
      _orgChartLoadError:
        (err && err.message) || el.call('polyglot', 'workspace.orgChart.loadFailed')
    })
  }
}

// Set (or clear, managerId falsy) one row's pending manager choice. Writes
// a NEW _orgChartWorking object — an in-place nested mutation would not
// reliably re-render the row's own select.
// el.call('orgChartSetWorking', s, partyId, managerId)
export function orgChartSetWorking(s, partyId, managerId) {
  const root = s.root
  const next = { ...(root._orgChartWorking || {}) }
  if (managerId) next[partyId] = managerId
  else delete next[partyId]
  root.update({ _orgChartWorking: next })
}

// Diff the working copy against the last-loaded edges — only genuinely
// changed rows go over the wire. [] means nothing to save.
// el.call('orgChartDiff')
export function orgChartDiff() {
  const root = this && this.state && this.state.root
  const working = root?._orgChartWorking || {}
  const original = orgChartManagerOf.call(this)
  const directory = orgChartDirectory.call(this)
  const out = []
  for (const p of directory) {
    const before = original.get(p.partyId) || null
    const after = working[p.partyId] || null
    if (before !== after) out.push({ partyId: p.partyId, managerId: after })
  }
  return out
}

// True when the working copy differs from the last-loaded edges — drives
// the Save button's enabled state.
// el.call('orgChartIsDirty')
export function orgChartIsDirty() {
  return orgChartDiff.call(this).length > 0
}

// Best-effort: swap a bare 24-hex Mongo id embedded in a server validation
// message for the matching person's name, so "party not found in this
// workspace: 68a1..." reads as a name. Falls back to the raw message when
// nothing matches — never throws on a shape it doesn't expect.
const _humanizeIds = (message, directory) => {
  if (!message) return message
  const byId = new Map(directory.map((p) => [p.partyId, p.name]))
  return message.replace(/[a-f0-9]{24}/gi, (id) => byId.get(id) || id)
}

// el.call('orgChartSave', s)
export async function orgChartSave(s) {
  const el = this
  const root = s.root
  const assignments = orgChartDiff.call(this)
  if (!assignments.length) return true
  root.update({ _orgChartSaving: true, _orgChartSaveError: null })
  const workspaceId = this.call('activeWorkspaceId', root)
  try {
    const parties = await el.sdk.getService('parties')
    await parties.setManagers(assignments, { workspaceId })
    const edges = await parties.listAllRelationships({ kind: 'reports_to' }, { workspaceId })
    root.update({ _orgChartEdges: edges || [], _orgChartSaving: false })
    return true
  } catch (err) {
    const directory = orgChartDirectory.call(this)
    root.update({
      _orgChartSaving: false,
      _orgChartSaveError: _humanizeIds(
        (err && err.message) || el.call('polyglot', 'workspace.orgChart.saveFailed'),
        directory
      )
    })
    return false
  }
}
